// src/pages/qc/PageQCCoolantSpecs.jsx
import { useState, useEffect, useMemo } from "react";
import * as api from "../../apiService";
import { useToast } from "../../components/Toast";

function fmt(min, max, unit) {
  if (min == null && max == null) return "—";
  if (min != null && max != null) return `${min} – ${max}${unit ? " " + unit : ""}`;
  if (max != null) return `≤ ${max}${unit ? " " + unit : ""}`;
  return `≥ ${min}${unit ? " " + unit : ""}`;
}

function CoolantSpecModal({ spec, onClose, onSaved }) {
  const showToast = useToast();
  const blank = {
    appearance: "", color: "",
    densityMin: "", densityMax: "",
    reserveAlkalinityMin: "", reserveAlkalinityMax: "",
    phMin: "", phMax: "",
    freezingPtMax: "",
  };
  const [productId, setProductId] = useState(spec?.productId ?? "");
  const [form,      setForm]      = useState(spec ? {
    appearance:             spec.appearance             ?? "",
    color:                  spec.color                  ?? "",
    densityMin:             spec.densityMin             ?? "",
    densityMax:             spec.densityMax             ?? "",
    reserveAlkalinityMin:   spec.reserveAlkalinityMin   ?? "",
    reserveAlkalinityMax:   spec.reserveAlkalinityMax   ?? "",
    phMin:                  spec.phMin                  ?? "",
    phMax:                  spec.phMax                  ?? "",
    freezingPtMax:          spec.freezingPtMax          ?? "",
  } : blank);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  function num(v)    { return v === "" ? null : Number(v); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!productId.trim()) return setError("Product name is required.");
    setSaving(true); setError(null);
    try {
      const saved = await api.saveCoolantSpec(productId.trim(), {
        appearance:             form.appearance   || null,
        color:                  form.color        || null,
        densityMin:             num(form.densityMin),
        densityMax:             num(form.densityMax),
        reserveAlkalinityMin:   num(form.reserveAlkalinityMin),
        reserveAlkalinityMax:   num(form.reserveAlkalinityMax),
        phMin:                  num(form.phMin),
        phMax:                  num(form.phMax),
        freezingPtMax:          num(form.freezingPtMax),
      });
      showToast(spec ? "Spec updated" : "Spec saved");
      onSaved(saved);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  const isEdit = !!spec;

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ width: 560 }}>
        <div className="modal-header">
          <span>{isEdit ? "Edit Coolant Spec" : "Add Coolant Spec"}</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ display:"grid", gap:12 }}>
            {error && <div className="alert-warn">{error}</div>}

            <div className="form-row">
              <label className="form-label">Product Name *</label>
              <input className="form-input" value={productId}
                onChange={e => setProductId(e.target.value)}
                disabled={isEdit} required />
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              <div className="form-row">
                <label className="form-label">Appearance</label>
                <input className="form-input" value={form.appearance}
                  onChange={e => set("appearance", e.target.value)} />
              </div>
              <div className="form-row">
                <label className="form-label">Color</label>
                <input className="form-input" value={form.color}
                  onChange={e => set("color", e.target.value)} />
              </div>
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              <div className="form-row">
                <label className="form-label">Density Min (g/cc)</label>
                <input className="form-input" type="number" step="0.0001" value={form.densityMin}
                  onChange={e => set("densityMin", e.target.value)} />
              </div>
              <div className="form-row">
                <label className="form-label">Density Max (g/cc)</label>
                <input className="form-input" type="number" step="0.0001" value={form.densityMax}
                  onChange={e => set("densityMax", e.target.value)} />
              </div>
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              <div className="form-row">
                <label className="form-label">Reserve Alkalinity Min (mL HCl 0.1N)</label>
                <input className="form-input" type="number" step="0.01" value={form.reserveAlkalinityMin}
                  onChange={e => set("reserveAlkalinityMin", e.target.value)} />
              </div>
              <div className="form-row">
                <label className="form-label">Reserve Alkalinity Max</label>
                <input className="form-input" type="number" step="0.01" value={form.reserveAlkalinityMax}
                  onChange={e => set("reserveAlkalinityMax", e.target.value)} />
              </div>
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
              <div className="form-row">
                <label className="form-label">pH Min</label>
                <input className="form-input" type="number" step="0.01" value={form.phMin}
                  onChange={e => set("phMin", e.target.value)} />
              </div>
              <div className="form-row">
                <label className="form-label">pH Max</label>
                <input className="form-input" type="number" step="0.01" value={form.phMax}
                  onChange={e => set("phMax", e.target.value)} />
              </div>
              <div className="form-row">
                <label className="form-label">Freezing Point Max (°C)</label>
                <input className="form-input" type="number" step="0.1" value={form.freezingPtMax}
                  onChange={e => set("freezingPtMax", e.target.value)} />
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function PageQCCoolantSpecs({ myPerms }) {
  const [specs,   setSpecs]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [search,  setSearch]  = useState("");
  const [modal,   setModal]   = useState(null); // null | "add" | spec object
  const [hovCol,  setHovCol]  = useState(null);

  const canEdit = myPerms?.includes("QC_EDIT");

  useEffect(() => {
    api.fetchCoolantSpecs()
      .then(setSpecs)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return specs.filter(s => s.productId.toLowerCase().includes(q));
  }, [specs, search]);

  function handleSaved(saved) {
    setSpecs(prev => {
      const idx = prev.findIndex(s => s.productId === saved.productId);
      return idx >= 0
        ? prev.map(s => s.productId === saved.productId ? saved : s)
        : [...prev, saved].sort((a, b) => a.productId.localeCompare(b.productId));
    });
    setModal(null);
  }

  const headers = ["Product", "Appearance", "Color", "Density @ 20°C (g/cc)",
    "Reserve Alk. (mL HCl 0.1N)", "pH", "Freezing Point (°C)"];
  if (canEdit) headers.push("");

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Coolant Specifications</div>
          <div className="page-subtitle">Quality parameters per coolant / antifreeze product</div>
        </div>
      </div>

      <div className="action-bar">
        <input className="form-input" placeholder="Search product…" value={search}
          onChange={e => setSearch(e.target.value)} style={{ width: 240 }} />
        {canEdit && (
          <button className="btn btn-primary" onClick={() => setModal("add")}>
            + Add Spec
          </button>
        )}
      </div>

      {loading && <div style={{ color:"#aaa", fontSize:12 }}>Loading…</div>}
      {error   && <div className="alert-warn">Error: {error}</div>}

      {!loading && !error && (
        <div className="table-wrap">
          <table className="data-table" data-hov-col={hovCol ?? undefined} onMouseLeave={() => setHovCol(null)}>
            <thead>
              <tr>
                {headers.map((h, i) => (
                  <th key={i} onMouseEnter={() => setHovCol(i)}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((s, ri) => (
                <tr key={s.productId}>
                  <td onMouseEnter={() => setHovCol(0)} className="fw-600">{s.productId}</td>
                  <td onMouseEnter={() => setHovCol(1)}>{s.appearance ?? "—"}</td>
                  <td onMouseEnter={() => setHovCol(2)}>{s.color ?? "—"}</td>
                  <td onMouseEnter={() => setHovCol(3)}>{fmt(s.densityMin, s.densityMax)}</td>
                  <td onMouseEnter={() => setHovCol(4)}>{fmt(s.reserveAlkalinityMin, s.reserveAlkalinityMax)}</td>
                  <td onMouseEnter={() => setHovCol(5)}>{fmt(s.phMin, s.phMax)}</td>
                  <td onMouseEnter={() => setHovCol(6)}>
                    {s.freezingPtMax != null ? `≤ ${s.freezingPtMax}°C` : "—"}
                  </td>
                  {canEdit && (
                    <td onMouseEnter={() => setHovCol(7)}>
                      <button className="btn btn-secondary" style={{ padding:"2px 10px", fontSize:12 }}
                        onClick={() => setModal(s)}>
                        Edit
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={headers.length} style={{ textAlign:"center", color:"#aaa", padding:24 }}>
                  No specs found.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <CoolantSpecModal
          spec={modal === "add" ? null : modal}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}
    </>
  );
}
